import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AxiosInstance from '../../../helpers/AxiosInstance';
import PDFCover from '../../../components/PDFCover/PDFCover';
import SearchBar from '../../../components/Search/SearchBar';
import './CouncilBooks.css';

const CouncilBooks = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage] = useState(12); // Show 12 books per page for better performance

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get('/visitor/council-books/categories');
      setCategories(response || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async (categoryId) => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(`/visitor/council-books/category/${categoryId}`);
      setBooks(response || []);
      setFilteredBooks(response || []);
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
      setFilteredBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
    if (!term.trim()) {
      setFilteredBooks(books);
      return;
    }
    
    const filtered = books.filter(book => 
      book.title.toLowerCase().includes(term.toLowerCase()) ||
      book.year.toString().includes(term) ||
      'المجلس الأعلى للّغة العربيّة'.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredBooks(filtered);
  };

  // Calculate pagination
  const paginatedBooks = useMemo(() => {
    const startIndex = (currentPage - 1) * booksPerPage;
    const endIndex = startIndex + booksPerPage;
    return filteredBooks.slice(startIndex, endIndex);
  }, [filteredBooks, currentPage, booksPerPage]);

  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    fetchBooks(category.id);
  };

  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setShowPdfModal(true);
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    setSelectedBook(null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setBooks([]);
    setFilteredBooks([]);
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Get category color and icon based on category ID
  const getCategoryStyle = (categoryId) => {
    const styles = {
      'magazine': { color: '#4b8b3b', icon: 'fas fa-book-open' },
      'guides': { color: '#28a745', icon: 'fas fa-book' },
      'seminars': { color: '#17a2b8', icon: 'fas fa-users' },
      'awards': { color: '#ffc107', icon: 'fas fa-trophy' },
      'jalons': { color: '#6f42c1', icon: 'fas fa-language' },
      'science': { color: '#dc3545', icon: 'fas fa-microscope' },
      'speeches': { color: '#fd7e14', icon: 'fas fa-microphone' },
      'notebooks': { color: '#20c997', icon: 'fas fa-sticky-note' },
      'other': { color: '#6c757d', icon: 'fas fa-file-alt' },
      'indexes': { color: '#e83e8c', icon: 'fas fa-list' },
      'november70': { color: '#795548', icon: 'fas fa-calendar' }
    };
    
    return styles[categoryId] || { color: '#4b8b3b', icon: 'fas fa-book' };
  };

  if (loading && !selectedCategory) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">{t('councilBooks.loadingCategories')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div className="container mt-5">
        <div className="row">
          <div className="col-12">
            <div className="conseil-arabe-hero-section mb-5">
              <div className="conseil-arabe-hero-content">
                <h1 className="display-4 fw-bold mb-3">
                  <i className="fas fa-book-open me-3"></i>
                  {t('councilBooks.heroTitle')}
                </h1>
                <p className="lead mb-0">
                  {t('councilBooks.heroSubtitle')}
                </p>
              </div>
            </div>

            <div className="row">
              {categories.map((category) => {
                const style = getCategoryStyle(category.id);
                return (
                  <div key={category.id} className="col-md-6 col-lg-4 mb-4">
                    <div 
                      className="card h-100 border-0 position-relative overflow-hidden council-category-card" 
                      style={{ 
                        borderRadius: '20px',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleCategorySelect(category)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div className="card-body d-flex flex-column p-4">

                        {/* Category Title */}
                        <div className="text-center mb-3">
                          <h5 
                            className="card-title text-white mb-0 fw-bold" 
                            style={{ 
                              fontSize: '1.2rem',
                              lineHeight: '1.4',
                              minHeight: '2.8rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                            }}
                          >
                            {category.name_ar}
                          </h5>
                        </div>

                        {/* Category Description */}
                        <div className="text-center mb-4">
                          <p 
                            className="card-text text-white mb-0 fw-medium" 
                            style={{
                              fontSize: '0.9rem',
                              lineHeight: '1.4',
                              minHeight: '2.8rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
                              opacity: 0.95
                            }}
                          >
                            {category.description_ar}
                          </p>
                        </div>

                        {/* Action Section */}
                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center">
                            <span 
                              className="badge px-3 py-2 fw-medium"
                              style={{ 
                                borderRadius: '20px', 
                                fontSize: '0.8rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#333',
                                textShadow: 'none'
                              }}
                            >
                              <i className="fas fa-book me-1"></i>
                              {category.book_count} livres
                            </span>
                            <button 
                              className="btn btn-sm px-3 py-2 fw-medium"
                              style={{ 
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#333',
                                border: 'none',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                              }}
                            >
                              <i className="fas fa-arrow-right me-1"></i>
                              Explorer
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const style = getCategoryStyle(selectedCategory.id);

  return (
    <div className="container mt-5">
      <div className="row">
        <div className="col-12">
          {/* Header Section */}
          <div className="category-header mb-4">
            {/* Top Row: Back Button and Title */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <button 
                className="btn btn-outline-secondary d-flex align-items-center"
                onClick={handleBackToCategories}
                style={{
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                {t('councilBooks.backToCategories')}
              </button>
              
              <div className="text-center flex-grow-1">
                <h1 className="h2 fw-bold mb-1" style={{ color: '#2c5530' }}>
                  {selectedCategory.name_ar}
                </h1>
                <p className="text-muted mb-0 fs-6">
                  {filteredBooks.length} {t('councilBooks.booksAvailable')}
                </p>
              </div>
              
              <div style={{ width: '140px' }}></div> {/* Spacer for balance */}
            </div>
            
            {/* Bottom Row: Search Bar */}
            <div className="row justify-content-center">
              <div className="col-md-6 col-lg-4">
                <SearchBar
                  placeholder={t('councilBooks.searchBooks')}
                  onSearch={handleSearch}
                  value={searchTerm}
                  className="w-100"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="row justify-content-center">
              <div className="col-md-6 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">{t('councilBooks.loadingBooks')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="row">
                {paginatedBooks.length > 0 ? (
                  paginatedBooks.map((book) => (
                  <div key={book.id} className="col-md-6 col-lg-3 mb-4">
                  <div 
                    className="card h-100 border-0 position-relative overflow-hidden" 
                    style={{ 
                      borderRadius: '12px',
                      background: '#ffffff',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: '300px'
                    }}
                    onClick={() => handleBookSelect(book)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                    }}
                  >
                    {/* PDF Cover - slot tolerates different cover formats (centered, clipped) */}
                    <div className="position-relative council-book-cover-wrap" style={{ height: '200px', overflow: 'hidden' }}>
                      <PDFCover 
                        pdfUrl={`/api/visitor/council-books/pdf/${encodeURIComponent(book.id)}`}
                        width={250}
                        height={200}
                        className="w-100"
                        alt={book.title}
                        lazy={true}
                      />
                    </div>
                    
                    {/* Book Details */}
                    <div className="card-body p-3">
                      {/* Book Title */}
                      <div className="mb-2">
                        <h6 
                          className="card-title text-dark mb-1 fw-bold" 
                          style={{ 
                            fontSize: '1rem',
                            lineHeight: '1.3',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {book.title}
                        </h6>
                      </div>

                      {/* Book Details */}
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-calendar text-muted me-2" style={{ fontSize: '0.8rem' }}></i>
                          <small className="text-muted">{book.year}</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-user text-muted me-2" style={{ fontSize: '0.8rem' }}></i>
                          <small className="text-muted">المجلس الأعلى للّغة العربيّة</small>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="d-grid">
                        <button 
                          className="btn btn-sm fw-medium"
                          style={{ 
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            padding: '0.5rem',
                            backgroundColor: '#2c5530',
                            borderColor: '#2c5530',
                            color: 'white',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#1e3a21';
                            e.target.style.borderColor = '#1e3a21';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#2c5530';
                            e.target.style.borderColor = '#2c5530';
                          }}
                        >
                          <i className="fas fa-book-open me-1"></i>
                          {t('councilBooks.readPdf')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h4 className="text-muted">{t('councilBooks.noResults')}</h4>
                    <p className="text-muted">{t('councilBooks.tryDifferentSearch')}</p>
                  </div>
                )}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="row mt-4">
                  <div className="col-12">
                    <nav aria-label="Books pagination">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{ 
                              backgroundColor: '#2c5530', 
                              borderColor: '#2c5530', 
                              color: 'white',
                              borderRadius: '8px',
                              margin: '0 2px'
                            }}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => handlePageChange(page)}
                              style={{ 
                                backgroundColor: currentPage === page ? '#1e3a21' : '#2c5530',
                                borderColor: currentPage === page ? '#1e3a21' : '#2c5530',
                                color: 'white',
                                borderRadius: '8px',
                                margin: '0 2px'
                              }}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{ 
                              backgroundColor: '#2c5530', 
                              borderColor: '#2c5530', 
                              color: 'white',
                              borderRadius: '8px',
                              margin: '0 2px'
                            }}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                    
                    <div className="text-center mt-2">
                      <small className="text-muted">
                        Showing {((currentPage - 1) * booksPerPage) + 1} to {Math.min(currentPage * booksPerPage, filteredBooks.length)} of {filteredBooks.length} books
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {showPdfModal && selectedBook && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-book-open me-2"></i>
                  {selectedBook.title}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closePdfModal}
                ></button>
              </div>
              <div className="modal-body p-0">
                <iframe
 amazin                  src={`/api/visitor/council-books/pdf/${encodeURIComponent(selectedBook.id)}`}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                  title={selectedBook.title}
                />
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closePdfModal}
                >
                  <i className="fas fa-times me-1"></i>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouncilBooks;